/**
 * Recent Jobs Component
 * Display latest job postings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import careerService from '../../services/careerService';
import './RecentJobs.css';

const RecentJobs = ({ limit = 10, region = null }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecentJobs();
  }, [limit, region]);

  const loadRecentJobs = async () => {
    try {
      setLoading(true);
      const data = await careerService.getRecentJobs({ limit, region });
      setJobs(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading recent jobs:', err);
      setError('Failed to load recent jobs');
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffDays = Math.floor((now - posted) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const formatSalary = (job) => {
    if (!job.salary || !job.salary.disclosed) return null;
    
    const { min, max, currency } = job.salary;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    }
    if (min) {
      return `From ${formatter.format(min)}`;
    }
    return null;
  };

  const getExperienceLabel = (level) => {
    const labels = {
      entry: 'Entry Level',
      junior: 'Junior',
      mid: 'Mid-Level',
      senior: 'Senior',
      lead: 'Lead',
      executive: 'Executive'
    };
    return labels[level] || level;
  };

  if (loading) {
    return (
      <div className="recent-jobs">
        <div className="jobs-loading">
          <div className="spinner"></div>
          <p>Loading recent jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-jobs">
      {/* Header */}
      <div className="jobs-header">
        <h2>
          <i className="fas fa-clock"></i>
          Recent Job Postings
        </h2>
        <button 
          className="btn-view-more"
          onClick={() => navigate('/career/job-search')}
        >
          View All Jobs
          <i className="fas fa-arrow-right"></i>
        </button>
      </div>

      {/* Jobs List */}
      {error ? (
        <div className="jobs-error">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="jobs-empty">
          <i className="fas fa-briefcase"></i>
          <p>No recent jobs found</p>
        </div>
      ) : (
        <div className="jobs-list">
          {jobs.map((job, index) => (
            <div 
              key={index} 
              className="job-card"
              onClick={() => window.open(job.source?.url, '_blank')}
            >
              {/* Job Header */}
              <div className="job-header-section">
                {job.company?.logo && (
                  <img 
                    src={job.company.logo} 
                    alt={job.company.name}
                    className="company-logo"
                  />
                )}
                <div className="job-title-section">
                  <h3 className="job-title">{job.title}</h3>
                  <p className="company-name">
                    {job.company?.name}
                    {job.company?.industry && (
                      <span className="industry-tag">
                        • {job.company.industry}
                      </span>
                    )}
                  </p>
                </div>
                <div className="job-meta">
                  <span className="time-ago">
                    <i className="fas fa-clock"></i>
                    {getTimeAgo(job.source?.postedDate)}
                  </span>
                </div>
              </div>

              {/* Job Details */}
              <div className="job-details">
                {/* Location */}
                <div className="detail-item location">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    {job.location?.city && `${job.location.city}, `}
                    {job.location?.country}
                  </span>
                  {job.location?.remote && (
                    <span className="remote-badge">
                      <i className="fas fa-laptop-house"></i>
                      {job.location.remoteType || 'Remote'}
                    </span>
                  )}
                </div>

                {/* Experience Level */}
                {job.experienceLevel && (
                  <div className="detail-item">
                    <i className="fas fa-signal"></i>
                    <span>{getExperienceLabel(job.experienceLevel)}</span>
                  </div>
                )}

                {/* Employment Type */}
                {job.employmentType && (
                  <div className="detail-item">
                    <i className="fas fa-briefcase"></i>
                    <span className="employment-type">
                      {job.employmentType.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Salary */}
                {formatSalary(job) && (
                  <div className="detail-item salary">
                    <i className="fas fa-dollar-sign"></i>
                    <span>{formatSalary(job)}</span>
                  </div>
                )}
              </div>

              {/* Skills */}
              {job.requiredSkills && job.requiredSkills.length > 0 && (
                <div className="job-skills">
                  {job.requiredSkills.slice(0, 5).map((skill, idx) => (
                    <span 
                      key={idx}
                      className={`skill-badge ${skill.importance}`}
                    >
                      {skill.name}
                    </span>
                  ))}
                  {job.requiredSkills.length > 5 && (
                    <span className="skill-badge more">
                      +{job.requiredSkills.length - 5} more
                    </span>
                  )}
                </div>
              )}

              {/* Badges */}
              <div className="job-badges">
                {job.metadata?.isGreenJob && (
                  <span className="badge green-badge">
                    <i className="fas fa-leaf"></i>
                    Green Job
                  </span>
                )}
                {job.metadata?.visaSponsor && (
                  <span className="badge visa-badge">
                    <i className="fas fa-passport"></i>
                    Visa Sponsor
                  </span>
                )}
                <span className="badge platform-badge">
                  {job.source?.platform}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentJobs;
